import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, DownloadIcon, FileUpIcon, RefreshCcwIcon } from 'lucide-react';
import { Button, Card, CardBody, Spinner } from '@material-tailwind/react';
import {
    useApplySecurityCsvImport,
    useDryRunSecurityCsvImport,
    useExportSecurityCsvTemplate,
} from '../../../custom-hooks/use-settings';
import { Notify, NotifyError } from '../../../utils/functions';
import { useAuth } from '../../../services/contexts/authContext';
import { STATIC_USER_ROLES } from '../../../utils/enums';

const getFileNameFromHeader = (headerValue) => {
    if (!headerValue) {
        return null;
    }

    const match = headerValue.match(/filename="?([^";]+)"?/i);
    return match ? match[1] : null;
};

const SettingsCsvTools = () => {
    const { userDetails } = useAuth();
    const [selectedFileName, setSelectedFileName] = useState('');
    const [csvContent, setCsvContent] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [confirmationText, setConfirmationText] = useState('');

    const exportMutation = useExportSecurityCsvTemplate();
    const dryRunMutation = useDryRunSecurityCsvImport();
    const applyMutation = useApplySecurityCsvImport();

    const isBusy = exportMutation.isLoading || dryRunMutation.isLoading || applyMutation.isLoading;

    const validationErrors = validationResult?.errors || [];
    const isValidationPassed = Boolean(validationResult?.isValid);

    const summary = useMemo(() => {
        return validationResult?.summary || {
            totalRows: 0,
            totalContracts: 0,
            totalBookings: 0,
            contractsWithIssues: 0,
        };
    }, [validationResult]);

    const handleExportTemplate = async () => {
        try {
            const response = await exportMutation.mutateAsync();
            const fileName = getFileNameFromHeader(response?.headers?.['content-disposition']) || 'contract-booking-security-template.csv';

            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);

            Notify('CSV template exported successfully', 'success');
        } catch (error) {
            NotifyError('Failed to download CSV template', error);
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            const content = await file.text();
            setSelectedFileName(file.name);
            setCsvContent(content);
            setValidationResult(null);
            setConfirmationText('');
        } catch (error) {
            NotifyError('Failed to read selected CSV file', error);
        }
    };

    const handleDryRun = async () => {
        if (!csvContent) {
            Notify('Select a CSV file first', 'warn');
            return;
        }

        const response = await dryRunMutation.mutateAsync({ csvContent });
        setValidationResult(response?.data || null);
        setConfirmationText('');

        if (response?.data?.isValid) {
            Notify('CSV validation passed. You can apply import now.', 'success');
        }
    };

    const handleApplyImport = async () => {
        if (!csvContent) {
            Notify('Select a CSV file first', 'warn');
            return;
        }

        if (!validationResult?.isValid) {
            Notify('Run dry-run validation and fix errors before applying', 'warn');
            return;
        }

        if (confirmationText.trim() !== 'APPLY') {
            Notify('Type APPLY to confirm import', 'warn');
            return;
        }

        const response = await applyMutation.mutateAsync({ csvContent });
        setValidationResult((previous) => ({
            ...(previous || {}),
            appliedSummary: response?.data?.summary || null,
        }));
    };

    const hasAccess = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN || 
                      userDetails?.role === STATIC_USER_ROLES.ADMIN ||
                      (userDetails?.role === STATIC_USER_ROLES.STAFF && userDetails?.dynamicRole?.permissions?.includes('FX22-2:manage'));

    if (!hasAccess) {
        return (
            <div className='flex min-h-[60vh] flex-col items-center justify-center p-8'>
                <div className='rounded-full bg-blue-gray-50 p-6'>
                    <AlertTriangleIcon size={48} className='text-blue-gray-300' />
                </div>
                <h2 className='mt-6 text-xl font-semibold text-blue-gray-800'>No Settings Available</h2>
                <p className='mt-2 text-center text-sm text-blue-gray-500'>
                    Your current role does not have access to this settings page.
                </p>
            </div>
        );
    }

    return (
        <div className='p-4 md:p-8 space-y-6'>
            <div className='relative overflow-hidden rounded-2xl border border-blue-gray-100 bg-gradient-to-br from-zinc-50 via-white to-zinc-100 p-6'>
                <div className='absolute -top-14 -right-8 h-40 w-40 rounded-full bg-zinc-200/50 blur-2xl' />
                <div className='relative'>
                    <p className='text-xs uppercase tracking-[0.2em] text-blue-gray-500'>Migration Tools</p>
                    <h1 className='mt-1 text-2xl font-semibold text-blue-gray-900'>Contract Security CSV Tools</h1>
                    <p className='mt-2 text-sm text-blue-gray-600'>
                        Export a template, edit booking-level security values, run dry-run checks, then apply changes safely.
                    </p>
                </div>
            </div>

            <Card className='border border-blue-gray-100 shadow-sm'>
                <CardBody className='space-y-4'>
                    <h2 className='text-lg font-semibold text-blue-gray-900'>1. Export Template</h2>
                    <p className='text-sm text-blue-gray-600'>
                        The exported CSV includes required columns: contractId, contractTotalSecurity, bookingId, bookingSecurityAmount.
                    </p>
                    <Button
                        className='rounded-full bg-black text-white flex items-center gap-2'
                        onClick={handleExportTemplate}
                        disabled={isBusy}
                    >
                        {exportMutation.isLoading ? <Spinner className='h-4 w-4' /> : <DownloadIcon size={16} />}
                        Download CSV
                    </Button>
                </CardBody>
            </Card>

            <Card className='border border-blue-gray-100 shadow-sm'>
                <CardBody className='space-y-4'>
                    <h2 className='text-lg font-semibold text-blue-gray-900'>2. Upload And Validate</h2>
                    <div className='rounded-xl border border-dashed border-blue-gray-200 p-4 bg-blue-gray-50/40'>
                        <label className='text-sm font-medium text-blue-gray-700'>Select CSV File</label>
                        <input
                            type='file'
                            accept='.csv,text/csv'
                            onChange={handleFileChange}
                            disabled={isBusy}
                            className='mt-2 block w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm'
                        />
                        {selectedFileName ? (
                            <p className='mt-2 text-xs text-blue-gray-600'>Selected: {selectedFileName}</p>
                        ) : null}
                    </div>

                    <div className='flex flex-wrap gap-3'>
                        <Button
                            className='rounded-full bg-black text-white flex items-center gap-2'
                            onClick={handleDryRun}
                            disabled={isBusy || !csvContent}
                        >
                            {dryRunMutation.isLoading ? <Spinner className='h-4 w-4' /> : <RefreshCcwIcon size={16} />}
                            Run Dry-Run Checks
                        </Button>
                    </div>

                    {validationResult ? (
                        <div className='space-y-4'>
                            <div className={`rounded-xl border p-4 ${isValidationPassed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                                <div className='flex items-center gap-2'>
                                    {isValidationPassed ? <CheckCircle2Icon size={18} className='text-green-700' /> : <AlertTriangleIcon size={18} className='text-red-700' />}
                                    <p className={`text-sm font-semibold ${isValidationPassed ? 'text-green-700' : 'text-red-700'}`}>
                                        {isValidationPassed ? 'Validation Passed' : 'Validation Failed'}
                                    </p>
                                </div>
                                <div className='mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs'>
                                    <div className='rounded-lg bg-white p-2 border border-blue-gray-100'>Rows: {summary.totalRows}</div>
                                    <div className='rounded-lg bg-white p-2 border border-blue-gray-100'>Contracts: {summary.totalContracts}</div>
                                    <div className='rounded-lg bg-white p-2 border border-blue-gray-100'>Bookings: {summary.totalBookings}</div>
                                    <div className='rounded-lg bg-white p-2 border border-blue-gray-100'>Contracts With Issues: {summary.contractsWithIssues}</div>
                                </div>
                            </div>

                            {validationErrors.length > 0 ? (
                                <div className='rounded-xl border border-red-200 bg-white p-4'>
                                    <p className='text-sm font-semibold text-red-700'>Validation Errors ({validationErrors.length})</p>
                                    <div className='mt-2 max-h-64 overflow-auto space-y-2'>
                                        {validationErrors.slice(0, 50).map((error, index) => (
                                            <div key={`${error.code}-${error.line}-${index}`} className='rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700'>
                                                Line {error.line || 0}: {error.message}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </CardBody>
            </Card>

            <Card className='border border-blue-gray-100 shadow-sm'>
                <CardBody className='space-y-4'>
                    <h2 className='text-lg font-semibold text-blue-gray-900'>3. Apply Import</h2>
                    <p className='text-sm text-blue-gray-600'>
                        Apply is only enabled when dry-run passes. This updates booking security values and synchronizes contract security totals.
                    </p>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                        <div>
                            <label className='text-xs font-semibold text-blue-gray-600'>Type APPLY to confirm</label>
                            <input
                                type='text'
                                value={confirmationText}
                                onChange={(event) => setConfirmationText(event.target.value)}
                                disabled={isBusy || !isValidationPassed}
                                placeholder='APPLY'
                                className='mt-1 block w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm'
                            />
                        </div>
                        <div className='flex items-end'>
                            <Button
                                className='rounded-full bg-red-700 text-white flex items-center gap-2'
                                onClick={handleApplyImport}
                                disabled={isBusy || !isValidationPassed || confirmationText.trim() !== 'APPLY'}
                            >
                                {applyMutation.isLoading ? <Spinner className='h-4 w-4' /> : <FileUpIcon size={16} />}
                                Apply CSV Import
                            </Button>
                        </div>
                    </div>

                    {validationResult?.appliedSummary ? (
                        <div className='rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800'>
                            Import applied. Updated {validationResult.appliedSummary.updatedBookings} bookings across {validationResult.appliedSummary.updatedContracts} contracts.
                        </div>
                    ) : null}
                </CardBody>
            </Card>
        </div>
    );
};

export default SettingsCsvTools;
